<template>
  <div>
    <b-card no-body class="mb-3" v-if="tags.length > 0">
      <b-card-header header-tag="header" class="p-0 info-collapse" role="tab">
        <b-button block href="#" v-b-toggle.infoline>
          <slot name="btn-content">
            {{ $t("template.quill_editor_variables_edit_title") }}
          </slot>
        </b-button>
      </b-card-header>
      <slot name="tags" :tags="tags" :tagsFull="tagsFull">
        <b-collapse id="infoline" visible accordion="my-accordion" role="tabpanel">
          <b-card-body>
            <div class="tags-list" ref="tagsListEl" :class="{ expanded: tagsFull && !simple }">
              <div class="tags-list__intro">
                <slot name="description">
                  {{ $t("template.quill_editor_variables_var_description") }}
                </slot>
              </div>
              <div v-for="(tag, index) in tags" :key="index" class="tag">
                <p>
                  <span @click="addTag(tag.value)" class="tag__label">
                    {{ tag.value }}
                  </span>
                  {{ tag.text }}
                </p>
              </div>
            </div>

            <div @click="tagsFull = !tagsFull" class="tag-list-all" v-if="!simple && !hideMoreBtn">
              <slot name="tags-full-text" :tagsFull="tagsFull">
                {{
                  tagsFull
                    ? $t("template.quill_editor_variables_show_description", "Show")
                    : $t("template.quill_editor_variables_hide_description", "Hide")
                }}
              </slot>
            </div>
          </b-card-body>
        </b-collapse>
      </slot>
    </b-card>
    <b-alert v-if="failErrors.length > 0" variant="danger" show>
      {{
        $t(
          "template.quill_editor_variables_fail_errors",
          "You have {count} incorrect variable: {variables} ::: You have {count} incorrect variables: {variables}",
          {
            count: failErrors.length,
            variables: new Intl.ListFormat(lang, { style: "long", type: "conjunction" }).format(
              failErrors
            ),
          },
          failErrors.length
        )
      }}
    </b-alert>
    <div class="mb-3">
      <slot
        name="between-tags-and-editor"
        :value="value"
        :unwrappedValue="unwrappedValue"
        :unwrappedValueInline="unwrappedValueInline"
      />
    </div>
    <div
      class="qev-editor"
      :class="{ 'qev-editor--fullscreen': fullscreen, 'qev-editor--simple': simple }"
    >
      <button
        type="button"
        class="qev-editor__fullscreen-toggle"
        :title="fullscreenToggleText"
        :aria-label="fullscreenToggleText"
        @click="toggleFullscreen"
      >
        <b-icon :icon="fullscreen ? 'fullscreen-exit' : 'arrows-fullscreen'" />
      </button>
      <quill-editor
        v-if="!simple"
        :value="value"
        @input="onQuillEditorInput"
        class="quill-editor-variables"
        :id="'quill-editor-variables'"
        ref="QuillEditorVariables"
        :options="editorOptionsValue"
        :disabled="disabled"
        :emitWithChangeSource="emitWithChangeSource"
        :editor-direction="editorDirection"
      ></quill-editor>
      <b-textarea
        v-else
        :value="value"
        @input="onTextAreaChange($event)"
        ref="TextAreaVariables"
        rows="10"
        class="textarea-editor"
        :style="{ '--editor-direction': editorDirection }"
        :readonly="disabled"
      ></b-textarea>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import CustomVideo from "@/components/vue-quill-editor/formats/custom-video";
import { unwrapContentInline, unwrapContentValue } from "@/components/vue-quill-editor/utils";

export default {
  data() {
    return {
      tagsFull: true,
      hideMoreBtn: false,
      failErrors: [],
      fullscreen: false,
    };
  },
  props: {
    value: {
      type: [String, Object],
      default: "",
    },
    editorOptions: {
      type: Object,
      default: () => {
        return {
          modules: {
            toolbar: {
              container: [
                ["bold", "underline", "strike", "italic"],
                ["blockquote"],
                [{ list: "ordered" }, { list: "bullet" }],
                [{ size: ["small", false, "large", "huge"] }],
                [{ align: [] }],
                [{ color: [] }, { background: [] }],
                ["image"],
                [CustomVideo.blotName],
                ["link"],
                ["clean"],
              ],
            },
            imageResize: {
              modules: ["Resize", "DisplaySize"],
              displayStyles: {
                backgroundColor: "black",
                border: "none",
                color: "white",
              },
            },
          },
        };
      },
    },
    editorOptionsDisabled: {
      type: Object,
      default: () => {
        return {
          modules: {
            toolbar: false,
          },
        };
      },
    },
    tags: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    simple: {
      type: Boolean,
      default: false,
    },
    valid: {
      type: Boolean,
      default: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    emitWithChangeSource: Boolean,
    editorDirection: {
      type: String,
      default: "ltr",
    },
  },
  computed: {
    ...mapState({
      lang: (state) => state.translation.locate,
    }),
    editorOptionsValue() {
      if (this.disabled) {
        return this.editorOptionsDisabled;
      }
      return this.editorOptions;
    },
    unwrappedValueInline() {
      return unwrapContentInline(this.value);
    },
    unwrappedValue() {
      return unwrapContentValue(this.value);
    },
    fullscreenToggleText() {
      return this.fullscreen
        ? this.$t("template.quill_editor_variables_fullscreen_exit", "Exit full screen")
        : this.$t("template.quill_editor_variables_fullscreen_open", "Full screen");
    },
  },
  watch: {
    tags: {
      handler(val) {
        if (val.length) {
          this.hideMoreBtn = val.length <= 3;
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.validateAndFillErrors(this.unwrappedValue);
  },
  beforeDestroy() {
    this.exitFullscreen();
  },
  methods: {
    toggleFullscreen() {
      if (this.fullscreen) {
        this.exitFullscreen();
      } else {
        this.enterFullscreen();
      }
    },
    enterFullscreen() {
      if (this.fullscreen) {
        return;
      }
      this.fullscreen = true;
      document.body.classList.add("qev-fullscreen-open");
      // Capture phase: keep Escape from bubbling up to the surrounding modal.
      document.addEventListener("keydown", this.onFullscreenKeydown, true);
    },
    exitFullscreen() {
      if (!this.fullscreen) {
        return;
      }
      this.fullscreen = false;
      document.body.classList.remove("qev-fullscreen-open");
      document.removeEventListener("keydown", this.onFullscreenKeydown, true);
    },
    onFullscreenKeydown(event) {
      if (event.key !== "Escape") {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      this.exitFullscreen();
    },
    tokenize(input) {
      const tokens = [];
      let currentToken = "";
      let insideTag = false;
      const bracketStack = [];

      for (let i = 0; i < input.length; i++) {
        const char = input[i];

        if (char === "<" && bracketStack.length === 0) {
          if (currentToken) {
            processCurrentToken();
          }
          insideTag = true;
          currentToken += char;
        } else if (char === ">" && insideTag) {
          currentToken += char;
          tokens.push({ type: "WORD", value: currentToken.trim() });
          currentToken = "";
          insideTag = false;
        } else if (char === "[" && !insideTag) {
          if (bracketStack.length === 0 && currentToken) {
            processCurrentToken();
          }
          bracketStack.push(i);
          currentToken += char;
        } else if (char === "]") {
          if (bracketStack.length > 0) {
            currentToken += char;
            bracketStack.pop();
            if (bracketStack.length === 0) {
              tokens.push({ type: "VARIABLE", value: currentToken.slice(1, -1).trim() });
              currentToken = "";
            }
          } else {
            if (currentToken) {
              processCurrentToken();
            }
            tokens.push({ type: "ERROR", value: "]" });
          }
        } else {
          currentToken += char;
        }
      }

      if (currentToken) {
        processCurrentToken();
      }

      // Add empty error tokens if needed
      while (tokens.filter((t) => t.type === "ERROR").length < 3) {
        tokens.push({ type: "ERROR", value: "" });
      }

      return tokens;

      function processCurrentToken() {
        if (bracketStack.length > 0) {
          // This is an error token
          tokens.push({ type: "ERROR", value: currentToken.trim() });
        } else if (currentToken.includes("[")) {
          // This token contains an unclosed bracket, so it's an error
          const parts = currentToken.split("[");
          if (parts[0].trim()) {
            tokens.push({ type: "WORD", value: parts[0].trim() });
          }
          tokens.push({ type: "ERROR", value: "[" + parts[1].trim() });
        } else if (currentToken.trim()) {
          tokens.push({ type: "WORD", value: currentToken.trim() });
        }
        currentToken = "";
      }
    },
    addTag(value) {
      if (!this.simple) {
        const quill = this.$refs.QuillEditorVariables.quill;
        const idx = quill.getSelection(true);
        quill.insertText(idx.index, value, "user");
        quill.setSelection(quill.getLength(), 0);
      } else {
        this.insertAtCursor(this.$refs.TextAreaVariables, value);
      }
    },
    onTextAreaChange(event) {
      this.$emit("input", event);
    },
    variablesValidation(value) {
      return new Promise((resolve) => {
        const tokens = this.tokenize(value);
        const failErrors = tokens
          .filter((token) => token.type === "ERROR" && Boolean(token.value))
          .map((token) => token.value);

        resolve(failErrors);
      });
    },
    validateAndFillErrors(value) {
      this.$emit("update:loading", true);
      this.variablesValidation(value)
        .then((failErrors) => {
          this.failErrors = failErrors;
          this.$emit("update:valid", failErrors.length === 0);
          return failErrors;
        })
        .finally(() => {
          this.$emit("update:loading", false);
        });
    },
    onQuillEditorInput(event) {
      this.validateAndFillErrors(event.value);
      this.$emit("input", event);
    },
    insertAtCursor(myField, myValue) {
      //IE support
      if (document.selection) {
        myField.focus();
        const sel = document.selection.createRange();
        sel.text = myValue;
      }
      //MOZILLA and others
      else if (myField.selectionStart || myField.selectionStart == "0") {
        const startPos = myField.selectionStart;
        const endPos = myField.selectionEnd;
        this.onTextAreaChange(
          myField.value.substring(0, startPos) +
            myValue +
            myField.value.substring(endPos, myField.value.length)
        );
      } else {
        this.onTextAreaChange(myValue);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.info-collapse {
  background-color: #ffffff;
  // border: none;
  > a.btn {
    color: lighten(#107598, 10%);
    font-family: "VitoExtended-Light";
    background-color: #ffffff;
    border: 1px solid transparent;
    &:hover,
    &:focus,
    &:active {
      color: darken(#107598, 10%);
      background-color: #ffffff;
      border: 1px solid transparent;
    }
  }
}

.tags-list {
  margin-bottom: 20px;
  &.expanded {
    max-height: 270px;
    overflow: hidden;
  }
  &__intro {
    line-height: 1.2;
    margin-bottom: 20px;
  }
}

.tag {
  display: flex;
  flex-direction: row;
  > p {
    margin-bottom: 16px;
  }

  &__label {
    margin-right: 15px;
    padding: 5px 10px;
    border-radius: 3px;
    background-color: rgba(136, 186, 204, 0.4);
    border: 1px solid #2883a2;
    font-size: 14px;
    align-self: flex-start;
    // text-transform: uppercase;
    cursor: pointer;
  }
}
.tag-list-all {
  margin: 0 auto;
  height: 32px;
  width: 140px;
  border: 1px solid #2883a2;
  border-radius: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.textarea-editor {
  direction: var(--editor-direction, ltr);
}

.qev-editor {
  position: relative;

  // Reserve room on the Quill toolbar so the toggle never sits on top of a control.
  ::v-deep .ql-toolbar {
    padding-right: 44px;
  }

  &--simple ::v-deep .textarea-editor {
    padding-right: 44px;
  }

  &__fullscreen-toggle {
    position: absolute;
    top: 4px;
    right: 4px;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    border: none;
    border-radius: 4px;
    background-color: transparent;
    color: #444444;
    cursor: pointer;

    &:hover,
    &:focus {
      background-color: rgba(136, 186, 204, 0.4);
      color: #2883a2;
    }
  }

  &--fullscreen {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 1080;
    display: flex;
    flex-direction: column;
    background-color: #ffffff;
    overflow: hidden;
    overscroll-behavior: contain;

    // Let the editor take whatever height is left below its own toolbar.
    ::v-deep .b-overlay-wrap {
      flex: 1 1 auto;
      display: flex;
      min-height: 0;
    }

    ::v-deep .b-overlay-wrap > .quill-editor {
      flex: 1 1 auto;
      display: flex;
      flex-direction: column;
      min-width: 0;
      min-height: 0;
    }

    ::v-deep .quill-editor > .ql-container {
      flex: 1 1 auto;
      min-height: 0;
      overflow-y: auto;
    }

    > .textarea-editor {
      flex: 1 1 auto;
      height: auto;
      min-height: 0;
      resize: none;
    }
  }
}
</style>

<style lang="scss">
body.qev-fullscreen-open {
  overflow: hidden;
}
</style>
